// custom-size-presentation.ts — what the extra SHOWS: on screen, and to the client (§9).
//
// PURE, stateless module: it formats, it does not price. Every value it displays comes from
// `priceCustomSize`, which guarantees the client wording says exactly what the line bills.
//
// THREE DISPLAY DECISIONS
//
// 1. The ACTUAL delta appears ONLY when it differs from the billed delta: always showing both would
//    cast doubt on an ordinary line, and merging them would hide the minimum when it applies.
// 2. On the CLIENT side, the BILLED delta is written — the client pays what they read. The actual
//    delta is internal and stays on the input screen.
// 3. Composition labels are ENUMERATED, never derived from the catalogue name: a commercial name can
//    change without the composition moving, and a client document must not depend on marketing copy.

import type { CustomSizeRate } from "../../models/data.model";
import { engineMessage, keyParam } from "../core/messages";
import { miniCustomSizeM2, type CustomSizeResult, type DimensionsMm } from "./custom-size";
import { customSizeRateByRef } from "./stands";

/** Composition message key, per component. Enumerated — see display decision 3. */
const COMPOSITION_KEY: Record<string, string> = {
  COMPLET: "engineCustomSizeCompComplet",
  STRUCTURE: "engineCustomSizeCompStructure",
  TOILE: "engineCustomSizeCompToile",
};

/** Family message key (uppercase), for the title line. */
const FAMILY_KEY: Record<string, string> = {
  PHO: "engineCustomSizeFamilyPho",
  PAR: "engineCustomSizeFamilyPar",
  ROL: "engineCustomSizeFamilyRol",
  ORI: "engineCustomSizeFamilyOri",
};

/** Family message key (lowercase), for the catalogue base line. */
const FAMILY_KEY_LOWER: Record<string, string> = {
  PHO: "engineCustomSizeFamilyPhoLower",
  PAR: "engineCustomSizeFamilyParLower",
  ROL: "engineCustomSizeFamilyRolLower",
  ORI: "engineCustomSizeFamilyOriLower",
};

/**
 * Area in French format, three decimals (e.g. "6,900 m²"). Kept for table display helpers.
 * @param n - Area in m².
 * @returns The formatted area.
 */
export const m2 = (n: number) => `${n.toFixed(3).replace(".", ",")} m²`;
/**
 * Amount in French format, two decimals.
 * @param n - Amount in euros.
 * @returns The formatted amount.
 */
export const eur = (n: number) => `${n.toFixed(2).replace(".", ",")} €`;

/**
 * Face label message key, upper-case or lower-case form.
 * @param faces - Number of faces (1 or 2).
 * @param uppercase - True for the upper-case form.
 * @returns The message key.
 */
const faceKey = (faces: number, uppercase: boolean) =>
  faces === 2
    ? uppercase
      ? "engineCustomSizeFaceRvUpper"
      : "engineCustomSizeFaceRv"
    : uppercase
      ? "engineCustomSizeFaceRectoUpper"
      : "engineCustomSizeFaceRecto";

export interface TitleContext {
  base: DimensionsMm;
  final: DimensionsMm;
  rate: CustomSizeRate;
  /** Only `billedDeltaM2` is read: the contract is narrowed to what is used, so a read-back can pass
   *  what its computed line carries without rebuilding a complete result. */
  result: Pick<CustomSizeResult, "billedDeltaM2">;
}

/**
 * SELLSY WORDING — three lines, copy-pasteable once translated (§9).
 *
 * The first line is the PRODUCT as the client receives it; the next two say where the price comes
 * from. That transparency is deliberate.
 * @param context - Base and final formats, rate and billed area.
 * @returns The three wording lines (encoded engine messages).
 */
export function sellsyTitle(context: TitleContext): string[] {
  const familyKey = FAMILY_KEY[context.rate.family] ?? null;
  const familyLowerKey = FAMILY_KEY_LOWER[context.rate.family] ?? null;
  const compositionKey = COMPOSITION_KEY[context.rate.component] ?? null;
  const baseM2 = Math.round((context.base.L * context.base.H / 1e6) * 1000) / 1000;
  return [
    engineMessage("engineCustomSizeTitleLine", {
      family: familyKey ? keyParam(familyKey) : context.rate.family,
      // Dimensions as strings so Intl does not insert grouping spaces (3400, not 3 400).
      width: String(context.final.L),
      height: String(context.final.H),
      face: keyParam(faceKey(context.rate.faces, true)),
      composition: compositionKey ? keyParam(compositionKey) : context.rate.component,
    }),
    engineMessage("engineCustomSizeBaseLine", {
      family: familyLowerKey ? keyParam(familyLowerKey) : context.rate.family.toLowerCase(),
      width: String(context.base.L),
      height: String(context.base.H),
      face: keyParam(faceKey(context.rate.faces, false)),
      areaDec3: baseM2,
    }),
    engineMessage("engineCustomSizeSurchargeLine", {
      areaDec3: context.result.billedDeltaM2,
      priceDec2: context.rate.pv_m2,
    }),
  ];
}

export interface LineDisplay {
  /** Billed area, always displayed. */
  billedArea: string;
  /** Actual area — present ONLY when it differs from the billed one (display decision 1). */
  actualArea: string | null;
  /** Line badges, in display order, as engine messages to translate. Empty list = ordinary line. */
  badges: string[];
}

/** The part of the custom-size result needed to display the extra line. */
export type DisplayableState = Pick<
  CustomSizeResult, "actualDeltaM2" | "billedDeltaM2" | "minSurfaceApplied" | "appliedFloorPrice"
>;

/**
 * What the extra line shows in the input table (§9).
 * Badges follow the tool's other floors: a label, not a colour.
 * @param state - Result of the custom-size pricing.
 * @returns Billed area, actual area (when different) and badges.
 */
export function lineDisplay(state: DisplayableState): LineDisplay {
  const badges: string[] = [];
  // The AREA minimum and the EUROS floor are two distinct things and do not always bite together: a
  // delta of 1.2 m² clears the area minimum yet can still fall below the floor.
  if (state.minSurfaceApplied) badges.push(engineMessage("engineCustomSizeBadgeMinSurface", { min: miniCustomSizeM2() }));
  if (state.appliedFloorPrice) badges.push("engineCustomSizeBadgeFullRate");
  return {
    billedArea: m2(state.billedDeltaM2),
    actualArea: state.billedDeltaM2 !== state.actualDeltaM2 ? m2(state.actualDeltaM2) : null,
    badges,
  };
}

/**
 * COUNTERS (§9): the extra does NOT feed the print m² — it is not produced, only billed. It
 * follows its parent on pieces and weight, which are those of the product actually delivered.
 *
 * Deliberately trivial declarative function: it exists so the rule is READABLE and TESTABLE in one
 * place instead of scattered across three different counters.
 * @returns Which counters the extra feeds.
 */
export function countersContribution(): { m2Print: boolean; pieces: boolean; weight: boolean } {
  return { m2Print: false, pieces: true, weight: true };
}

/**
 * THE WORDING OF AN ALREADY PRICED LINE — the path taken by read-backs (client export, copy button).
 * `null` when the line is not an extra, or when its read-back data is missing.
 *
 * Uses the frozen `ratePvM2` from `compute()` when present so a later catalogue change cannot
 * rewrite the €/m² on a sent/frozen quote while `row.total` stays at the old amount. Live rate
 * lookup only fills family/composition labels; if the rate was removed, wording falls back to
 * SUR-MESURE (custom size) with the frozen price.
 * @param final - Final requested dimensions.
 * @param customSize - Custom-size data posted on the priced line.
 * @returns The wording lines, or `null` when not applicable.
 */
export function extraTitle(
  final: DimensionsMm | null | undefined,
  customSize:
    | {
        billedDeltaM2: number;
        rateRef: string;
        /** Frozen €/m² from pricing — preferred over live `CUSTOM_SIZE_RATES`. */
        ratePvM2?: number;
        base: DimensionsMm;
      }
    | null
    | undefined,
): string[] | null {
  if (!final || !customSize) return null;
  const live = customSizeRateByRef(customSize.rateRef);
  const pvM2 = customSize.ratePvM2 ?? live?.pv_m2;
  if (pvM2 == null) return null;

  if (live) {
    return sellsyTitle({
      base: customSize.base,
      final,
      rate: { ...live, pv_m2: pvM2 },
      result: { billedDeltaM2: customSize.billedDeltaM2 },
    });
  }

  const baseM2 = Math.round(((customSize.base.L * customSize.base.H) / 1e6) * 1000) / 1000;
  return [
    engineMessage("engineCustomSizeTitleFallback", { width: String(final.L), height: String(final.H) }),
    engineMessage("engineCustomSizeBaseLinePlain", {
      width: String(customSize.base.L),
      height: String(customSize.base.H),
      areaDec3: baseM2,
    }),
    engineMessage("engineCustomSizeSurchargeLine", {
      areaDec3: customSize.billedDeltaM2,
      priceDec2: pvM2,
    }),
  ];
}
